import * as genericService from "./genericFormServices";
import type { CreateAttachmentDto } from "../types/MainType.type";
import { formatSignatureUrl, type FetchParams } from "./NetworkService";
import type { LegalAdviceFormTenData } from "../types/LegalAdvices/LegaladviceFormTen.type";

const ENDPOINT = "legal-advice-form-ten";

export interface LegalAdviceFormTenFetchResponse {
  data: LegalAdviceFormTenData[];
  totalItems: number;
}

// ---------------------------------------------------------------------------
// 1. Mapper for Legal Advice Form Ten
// ---------------------------------------------------------------------------
export const mapLegalAdviceFormTenBackendToFrontend = (
  item: any
): LegalAdviceFormTenData => {
  if (!item) return {} as LegalAdviceFormTenData;

  const form = item.legalAdviceFormTen || item.formData || item;

  return {
    // Document Level Identifiers
    id: item.id || form.id || "",
    documentNumber: item.documentNumber || "",
    formType: item.formType || "LEGAL_ADVICE_FORM_TEN",
    status: item.status || form.status || "DRAFT",

    // Form-Specific Fields
    office: form.office || "",
    lawRegisterNo: form.lawRegisterNo || "",
    refNo: form.refNo || "",

    // Section 1: Case Details (1 to 8)
    courtName: form.courtName || "",
    appealCaseNo: form.appealCaseNo || form.caseNo || form.caseNumber || "",
    appellantName: form.appellantName || form.plaintiffName || "",
    respondentName: form.respondentName || form.defendantName || "",
    caseSummary: form.caseSummary || form.briefFacts || "",
    originalCourtDetails: form.originalCourtDetails || "",
    higherCourtDetails: form.higherCourtDetails || "",
    finalCourtDetails: form.finalCourtDetails || "",

    // Section 2: Opinion & Signatures (9 to 11)
    handlingOfficerArgumentSummary: form.handlingOfficerArgumentSummary || "",
    handlingOfficerOpinion: form.handlingOfficerOpinion || "",
    handlingOfficerName: form.handlingOfficerName || form.prosecutorName || "",
    handlingOfficerSubmitDate:
      form.handlingOfficerSubmitDate || form.prosecutorSubmissionDate || "",
    handlingOfficerSignature: formatSignatureUrl(
      form.handlingOfficerSignature || form.prosecutorSignatureImage
    ),

    inChargeOfficerOpinion:
      form.inChargeOfficerOpinion || form.responsibleOfficerOpinion || "",
    inChargeOfficerName: form.inChargeOfficerName || form.officerName || "",
    inChargeOfficerSubmitDate:
      form.inChargeOfficerSubmitDate || form.officerSubmissionDate || "",
    inChargeOfficerSignature: formatSignatureUrl(
      form.inChargeOfficerSignature || form.officerSignatureImage
    ),

    // Geo Levels
    originGeoLevel: item.originGeoLevel || "",
    targetGeoLevels: Array.isArray(item.targetGeoLevels)
      ? item.targetGeoLevels
      : [],

    // Attachments Array
    attachments: item.attachments || form.attachments || [],

    // Dates & Status Metadata
    rejectionReason: item.rejectionReason || form.rejectionReason || "",
    submitDate: form.submitDate || item.signedAt || item.updatedAt || "",
    createdAt: item.createdAt || form.createdAt || "",
    updatedAt: item.updatedAt || form.updatedAt || "",
    submittedAt: item.submittedAt || form.submittedAt || "",

    // Relational Metadata
    createdBy: item.createdBy
      ? {
          id: item.createdBy.id,
          name: item.createdBy.name,
          email: item.createdBy.email,
          role: item.createdBy.role,
          position: item.createdBy.position,
        }
      : undefined,
    originOffice: item.originOffice
      ? {
          id: item.originOffice.id,
          name: item.originOffice.name,
          code: item.originOffice.code,
        }
      : undefined,
    stageHistory: Array.isArray(item.stageHistory)
      ? item.stageHistory.map((history: any) => ({
          id: history.id,
          action: history.action,
          comments: history.comments,
          attachments: history.attachments,
          createdAt: history.createdAt,
          performedBy: history.performedBy
            ? {
                id: history.performedBy.id,
                name: history.performedBy.name,
                role: history.performedBy.role,
                position: history.performedBy.position,
                office: history.performedBy.office
                  ? {
                      id: history.performedBy.office.id,
                      name: history.performedBy.office.name,
                      code: history.performedBy.office.code,
                      level: history.performedBy.office.level,
                    }
                  : undefined,
              }
            : undefined,
        }))
      : [],
    auditLogs: Array.isArray(item.auditLogs) ? item.auditLogs : [],
  };
};

// ---------------------------------------------------------------------------
// 2. Custom Filter Function for Legal Advice Form Ten
// ---------------------------------------------------------------------------
const legalAdviceFormTenFilterPredicate = (
  app: LegalAdviceFormTenData,
  params: FetchParams
): boolean => {
  const { searchTerm, selectedStatus, selectedDate } = params;

  const matchesStatus = !selectedStatus || app.status === selectedStatus;
  const formattedAppDate = app.submitDate || app.createdAt || "";
  const matchesDate =
    !selectedDate || Boolean(formattedAppDate.includes(selectedDate));

  const term = (searchTerm || "").toLowerCase().trim();

  const matchesGeneralSearch =
    !term ||
    Boolean(app.documentNumber && app.documentNumber.toLowerCase().includes(term)) ||
    Boolean(app.lawRegisterNo && app.lawRegisterNo.toLowerCase().includes(term)) ||
    Boolean(app.appealCaseNo && app.appealCaseNo.toLowerCase().includes(term)) ||
    Boolean(app.courtName && app.courtName.toLowerCase().includes(term)) ||
    Boolean(app.appellantName && app.appellantName.toLowerCase().includes(term)) ||
    Boolean(app.respondentName && app.respondentName.toLowerCase().includes(term)) ||
    Boolean(app.handlingOfficerName && app.handlingOfficerName.toLowerCase().includes(term)) ||
    Boolean(app.inChargeOfficerName && app.inChargeOfficerName.toLowerCase().includes(term));

  return Boolean(matchesStatus && matchesDate && matchesGeneralSearch);
};

// ---------------------------------------------------------------------------
// 3. API Request Functions
// ---------------------------------------------------------------------------

export const fetchLegalAdviceFormTenApplications = (
  params: FetchParams
): Promise<LegalAdviceFormTenFetchResponse> =>
  genericService.fetchApplications<LegalAdviceFormTenData>(
    ENDPOINT,
    params,
    mapLegalAdviceFormTenBackendToFrontend,
    legalAdviceFormTenFilterPredicate
  );

export const fetchLegalAdviceFormTenById = (
  id: string
): Promise<LegalAdviceFormTenData> =>
  genericService.fetchApplicationById<LegalAdviceFormTenData>(
    ENDPOINT,
    id,
    mapLegalAdviceFormTenBackendToFrontend
  );

export const saveLegalAdviceFormTenDraft = (
  id: string | null,
  data: Partial<LegalAdviceFormTenData>
) => genericService.saveDraft<LegalAdviceFormTenData>(ENDPOINT, id, data);

export const submitLegalAdviceFormTenForm = (
  id: string,
  data: LegalAdviceFormTenData
) => genericService.submitForm<LegalAdviceFormTenData>(ENDPOINT, id, data);

export const deleteLegalAdviceFormTenForm = (id: string) =>
  genericService.deleteForm(ENDPOINT, id);

// ---------------------------------------------------------------------------
// 4. Superior Stage Actions
// ---------------------------------------------------------------------------
export const startReviewLegalAdviceForm = (id: string) =>
  genericService.startReviewForm(id);

export const attachLegalAdviceForm = (
  id: string,
  comments?: string,
  attachments?: CreateAttachmentDto[],
  targetGeoLevels?: string[]
) => genericService.attachForm(id, comments, attachments, targetGeoLevels);

export const uploadFileService = (file: File) =>
  genericService.uploadFile(file);